import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, GitCompareArrows } from "lucide-react";
import { track, events } from "../lib/analytics";
import { useCompareStash } from "../lib/compareStash";
import { useQuoteModal, inferCategoryFromPath } from "../lib/quoteModal";

/**
 * Iter-189.5 Sprint 4.5 — Mobile sticky bottom CTA for vertical hubs.
 *
 * The audit found that mobile visitors to vertical hubs
 * (/auto-insurance, /auto-loans, /refinance, /warranty) had no
 * always-visible CTA driving them into the compare/quote flow. The
 * existing `<CompareDrawer />` only appears AFTER the user has
 * stashed at least one carrier — this component shows BEFORE,
 * inviting the click.
 *
 * Behavior:
 *   - Visible only at <md width AND only on vertical hub paths
 *   - Hides automatically when the CompareDrawer becomes visible
 *     (`items.length > 0`) so we never double-stack sticky elements
 *   - Hides after the user dismisses it (sessionStorage per-tab)
 *   - Click → opens QuoteModal scoped to the page's vertical
 *   - Idle reveal: starts hidden, fades in after 1.2s so it doesn't
 *     compete with the hero on first paint
 */
const HUB_PATHS = new Set([
  "/auto-insurance",
  "/auto-loans",
  "/refinance",
  "/warranty",
  // Iter-274.M — Extend the sticky-compare CTA to the iter-274 apex
  // hubs. These are deep, high-intent SEO landing pages (3,000+ words
  // of educational content) where mobile readers benefit from an
  // always-visible compare CTA at the bottom of the viewport. Without
  // this addition, the page fell through to MobileStickyBar (z-40)
  // which uses generic CTA copy. The apex hubs warrant the same
  // dismissable, category-aware treatment as the base verticals.
  "/auto-insurance/business-use-coverage", // Cluster A (iter-274.G)
  "/auto-insurance/employer-requirements", // Cluster B (iter-274.H)
  "/auto-insurance/commercial-auto-coverage", // Cluster C (iter-274.J)
  "/auto-insurance/cost-and-claims-decisions", // Cluster E (iter-274.L)
]);

const STORAGE_KEY = "carsavr.mobile_sticky_compare_dismissed";

const COPY_BY_CATEGORY = {
  insurance: { eyebrow: "Compare", label: "Free quotes from top insurers" },
  "auto-loan-new": { eyebrow: "Compare", label: "Pre-qualify · soft credit pull" },
  "auto-loan-used": { eyebrow: "Compare", label: "Pre-qualify · soft credit pull" },
  refinance: { eyebrow: "Lower", label: "Lower my monthly payment" },
  warranty: { eyebrow: "Compare", label: "Editor-vetted warranty quotes" },
};

export default function MobileStickyCompareCTA() {
  const location = useLocation();
  const { items: stashItems } = useCompareStash();
  const { open: openQuoteModal } = useQuoteModal();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  // Read dismissed state once per mount
  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") {
        setDismissed(true);
      }
    } catch {
      /* swallow */
    }
  }, []);

  // Fade-in after 1.2s so it doesn't compete with hero on cold load
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // Only on hub paths
  if (!HUB_PATHS.has(location.pathname)) return null;
  // Hide when CompareDrawer is showing
  if (stashItems.length > 0) return null;
  // Hide when dismissed this session
  if (dismissed) return null;

  const category = inferCategoryFromPath(location.pathname);
  const copy = COPY_BY_CATEGORY[category] || COPY_BY_CATEGORY.insurance;

  const handleClick = () => {
    track(events.COMPARE_OFFERS_CLICK, {
      source: "mobile_sticky_compare_cta",
      category,
      page_path: location.pathname,
    });
    openQuoteModal(category);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* swallow */
    }
  };

  return (
    <div
      data-testid="mobile-sticky-compare-cta"
      className={`md:hidden fixed inset-x-0 bottom-0 z-[100] bg-[#0A2540] border-t border-[#10B981]/40 shadow-2xl transition-transform duration-300 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      role="region"
      aria-label="Quick compare"
    >
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={handleClick}
          data-testid="mobile-sticky-compare-cta-button"
          className="flex-1 flex items-center justify-between gap-2 px-4 py-3.5 text-left active:bg-brand-ink-press transition-colors"
        >
          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#10B981] mb-0.5">
              {copy.eyebrow}
            </p>
            <p className="text-sm font-bold text-white truncate">
              {copy.label}
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1.5 shrink-0 h-10 px-3 bg-[#10B981] text-[#0A2540] font-bold text-[10px] uppercase tracking-[0.16em]"
            aria-hidden
          >
            <GitCompareArrows className="w-3.5 h-3.5" />
            Start
            <ArrowRight className="w-3 h-3" />
          </span>
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="mobile-sticky-compare-cta-dismiss"
          aria-label="Dismiss quick compare"
          className="px-3 text-white/40 hover:text-white/80 border-l border-white/10"
        >
          ×
        </button>
      </div>
      {/* Helper link gives a non-modal path forward — for users who
          want to keep browsing the hub without opening the modal. */}
      <p className="hidden text-center text-[10px] text-white/50 pb-1">
        <Link to="/compare" data-testid="mobile-sticky-compare-cta-see-all">
          or browse side-by-side compare →
        </Link>
      </p>
    </div>
  );
}
